import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

import {
    DroneConfigDocument,
    useDroneConfigCollection,
    useUnavailableDroneNamesDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";

export default function DroneCreatePage() {
    const { organizationId, locationId } = useParams();
    const navigate = useNavigate();
    const [droneConfigs, isLoadingConfigs, errorConfigs] =
        useDroneConfigCollection([], { mode: "once", type: "map" });
    const droneConfigPresets = droneConfigs.presets;
    const [unavailableDroneNames, isLoadingUnavailableNames, errorNames] =
        useUnavailableDroneNamesDocument({ mode: "once" });

    const isLoading = isLoadingConfigs || isLoadingUnavailableNames;
    const error = errorConfigs || errorNames;

    const droneIds = useMemo(() => {
        return Object.keys(droneConfigs ?? {})
            .filter((id) => id !== "presets")
            .map((id) => id.toUpperCase());
    }, [droneConfigs]);

    const unavailableNames = useMemo(() => {
        return (unavailableDroneNames?.names ?? []).map((name) =>
            name.toUpperCase(),
        );
    }, [unavailableDroneNames]);

    const createDroneConfigSchema = useMemo(() => {
        return z.object({
            droneId: z
                .string()
                .transform((val) => val.toUpperCase())
                .refine((val) => /^[A-Z]{4}$/.test(val), {
                    message: "Drone name must be 4 letters (A-Z)",
                })
                .refine((val) => !droneIds.includes(val), {
                    message: "Drone name already exists",
                })
                .refine((val) => !unavailableNames.includes(val), {
                    message: "Drone name is unavailable",
                }),
        });
    }, [droneIds, unavailableNames]);

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(createDroneConfigSchema),
    });

    const onSubmit = async (formData) => {
        const droneId = formData.droneId;
        const droneConfig = {
            id: droneId,
            organizationId: organizationId,
            locationId: locationId,
            payloadId: "KaLPC6zGR5KXzJ3dKYL2",
            dockId: "none",
            augmentation: "none",
            docks: [],
            // The following are just for backwards compatibility and can be removed later
            focal_length:
                droneConfigPresets.E13.default.payload.camera_settings
                    .focal_length,
            sensor_height:
                droneConfigPresets.E13.default.payload.camera_settings
                    .sensor_height,
            sensor_width:
                droneConfigPresets.E13.default.payload.camera_settings
                    .sensor_width,
            max_focus_distance:
                droneConfigPresets.E13.default.payload.camera_settings
                    .max_focus_distance,
            min_focus_distance:
                droneConfigPresets.E13.default.payload.camera_settings
                    .min_focus_distance,
            z_offset:
                droneConfigPresets.E13.default.payload.camera_settings.z_offset,
            bufferDistance:
                droneConfigPresets.E13.default.routeplanner.buffer_distance,
            max_flight_duration:
                droneConfigPresets.E13.default.routeplanner.max_flight_duration,
            max_speed: droneConfigPresets.E13.default.routeplanner.max_speed,
            scan_speed: droneConfigPresets.E13.default.routeplanner.scan_speed,
            skipTakeOff:
                droneConfigPresets.E13.default.routeplanner.skip_take_off,
            takeoff_height:
                droneConfigPresets.E13.default.routeplanner.takeoff_height,
            takeoff_buffer:
                droneConfigPresets.E13.default.routeplanner.takeoff_buffer,

            // The following should stay
            ...droneConfigPresets.E13.default,
        };
        await DroneConfigDocument(droneId).set(droneConfig);
        navigate(urls.locationDrone(organizationId, locationId, droneId));
    };

    return (
        <Layout>
            <title>New Drone | MyCorvus</title>
            <Breadcrumbs path={breadcrumbPaths.droneCreate} />
            <Card>
                <Title
                    title={"Create new drone"}
                    backButtonUrl={urls.locationDrones(
                        organizationId,
                        locationId,
                    )}
                />

                <Card.Body className="p-4 py-0">
                    {error ? (
                        <Alert variant="danger" className="mx-4 mb-4">
                            Something went wrong. Please refresh the page.
                        </Alert>
                    ) : isLoading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            {
                                <TextFormGroup
                                    label="Drone name"
                                    name="droneId"
                                    control={control}
                                    errors={errors}
                                />
                            }
                            <Button
                                variant="success"
                                disabled={isSubmitting || !isDirty}
                                type="submit"
                                className="mb-3 mt-1"
                            >
                                <FaSave className="mb-1 me-1" />
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                            <CancelButton
                                className="ms-2 mb-3 mt-1"
                                to={urls.locationDrones(
                                    organizationId,
                                    locationId,
                                )}
                            />
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
